import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Send, Eye, TestTube, Loader2, CheckCircle2, XCircle, Newspaper,
  Home, Plus, X, Bold, Italic, Link as LinkIcon, List, Heading2, Users, Copy,
  Megaphone, Sparkles,
} from 'lucide-react';
import { authHeaders } from '../../context/AuthContext';
import WelcomeMailEditor from './WelcomeMailEditor';
import s from './adminStyles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NewsletterPanel — compose + broadcast to consented leads via Resend.
 *
 * Editor is a Markdown textarea with a tiny toolbar (bold / italic /
 * link / heading / list). Nothing fancy — email HTML is fragile so a
 * plain markdown source keeps output predictable across Gmail, Outlook,
 * and Apple Mail. Live preview renders the same HTML the backend will
 * ship.
 *
 * Insert panel lists newest blogs + newest properties. Clicking a card
 * appends a `[[BLOG:slug]]` / `[[PROPERTY:id]]` reference at the caret
 * inside the markdown — the backend expands those into real card blocks
 * at send time. Two independent counters control the "auto-append"
 * behaviour so the admin can layer manual picks and auto-recency.
 */

const REFRESH_INTERVAL_MS = 15000;

// Human-friendly labels for the lead-source segmentation chips. Fallback
// to the raw string if we haven't localised it — new form-sources ship
// their `source` as-is and appear correctly in the picker until we get
// around to adding a friendly label.
const SOURCE_LABELS = {
  homepage: 'Homepage opt-in',
  property_detail: 'Woning-aanvraag',
  retention_popup: 'Exit-intent popup',
  'koopplanner-spanje': 'Koopplanner (lead magnet)',
  'lead-magnet': 'Koopplanner (lead magnet)',
  onbekend: 'Onbekende bron',
};

const NewsletterPanel = () => {
  // Top-level tab: broadcast composer OR welcome-mail template editor.
  // Kept in local state (not URL) because both live under the same
  // admin route and admins toggle between them rapidly.
  const [tab, setTab] = useState('broadcast');

  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [body, setBody] = useState('');
  const [blogSlugs, setBlogSlugs] = useState([]);          // manually picked
  const [propertyIds, setPropertyIds] = useState([]);
  // Recommended defaults per mobile-compact guideline: 3 properties, 2 blogs.
  const [autoBlogs, setAutoBlogs] = useState(2);
  const [autoProperties, setAutoProperties] = useState(3);

  // New: audience language toggle (drives section labels + CTA copy).
  const [language, setLanguage] = useState('nl');
  // New: content-block order between intro (fixed first) and footer
  // (fixed last). Simple up/down UI so no drag-and-drop dependency.
  const [sectionOrder, setSectionOrder] = useState(['properties', 'blogs', 'download']);
  // New: optional CTA under the properties list.
  const [showAllPropertiesCta, setShowAllPropertiesCta] = useState(true);
  // New: optional free-download promo block.
  const [leadMagnetId, setLeadMagnetId] = useState('');    // '' = off
  const [leadMagnets, setLeadMagnets] = useState([]);

  const [recentBlogs, setRecentBlogs] = useState([]);
  const [recentProps, setRecentProps] = useState([]);
  const [recipientsCount, setRecipientsCount] = useState(null);
  const [sourceBreakdown, setSourceBreakdown] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]); // empty = alle bronnen

  const [previewHtml, setPreviewHtml] = useState('');
  const [isPreviewLoading, setPreviewLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [isTesting, setTesting] = useState(false);
  const [isBroadcasting, setBroadcasting] = useState(false);
  const [flash, setFlash] = useState(null);                // {type, text}
  const [history, setHistory] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const textareaRef = useRef(null);

  // ------------------------------------------------------------------ data
  const refreshHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletters`, { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        setHistory(d.items || []);
      }
    } catch (e) { /* silent — panel keeps prior list */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [rec, breakdown] = await Promise.all([
          fetch(`${API_URL}/api/admin/newsletters/recent-content?limit=8`, { headers: authHeaders() }),
          fetch(`${API_URL}/api/admin/newsletters/source-breakdown`, { headers: authHeaders() }),
        ]);
        if (rec.ok) {
          const d = await rec.json();
          setRecentBlogs(d.blogs || []);
          setRecentProps(d.properties || []);
          setLeadMagnets(d.lead_magnets || []);
        }
        if (breakdown.ok) {
          const d = await breakdown.json();
          setSourceBreakdown(d.items || []);
        }
      } catch (e) { /* ignored */ }
    })();
    refreshHistory();
    const t = setInterval(refreshHistory, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [refreshHistory]);

  // Recalculate recipient count whenever the source segmentation changes.
  // Debounced by React batching — a single fetch per click. Empty selection
  // means "everyone", matching the backend behaviour of `sources=None`.
  useEffect(() => {
    const url = selectedSources.length
      ? `${API_URL}/api/admin/newsletters/recipients-count?sources=${selectedSources.join(',')}`
      : `${API_URL}/api/admin/newsletters/recipients-count`;
    fetch(url, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setRecipientsCount(d.count); })
      .catch(() => { /* keep prior count */ });
  }, [selectedSources]);

  // ------------------------------------------------------------------ editor helpers
  const wrapSelection = (before, after = before) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = ta.value;
    const selected = value.slice(start, end) || 'tekst';
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    setBody(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  };

  const insertAtCaret = (text) => {
    const ta = textareaRef.current;
    if (!ta) { setBody((b) => `${b}\n${text}\n`); return; }
    const pos = ta.selectionStart;
    const value = ta.value;
    const prefix = pos > 0 && value[pos - 1] !== '\n' ? '\n\n' : '';
    const next = value.slice(0, pos) + prefix + text + '\n\n' + value.slice(pos);
    setBody(next);
    setTimeout(() => {
      ta.focus();
      const p = pos + prefix.length + text.length + 2;
      ta.setSelectionRange(p, p);
    }, 0);
  };

  const insertLink = () => {
    const url = window.prompt('URL:', 'https://casaothers.nl');
    if (!url) return;
    wrapSelection('[', `](${url})`);
  };

  // ------------------------------------------------------------------ picks
  const togglePickBlog = (slug) => {
    setBlogSlugs((prev) => prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]);
  };
  const togglePickProp = (id) => {
    setPropertyIds((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]);
  };

  const pickedBlogsResolved = useMemo(
    () => blogSlugs.map((slug) => recentBlogs.find((b) => b.slug === slug)).filter(Boolean),
    [blogSlugs, recentBlogs],
  );
  const pickedPropsResolved = useMemo(
    () => propertyIds.map((id) => recentProps.find((p) => String(p.id) === String(id))).filter(Boolean),
    [propertyIds, recentProps],
  );

  // ------------------------------------------------------------------ actions
  const buildPayload = () => ({
    subject: subject.trim(),
    body_markdown: body,
    preheader: preheader.trim(),
    blog_slugs: blogSlugs,
    property_ids: propertyIds.map(String),
    auto_latest_blogs: autoBlogs,
    auto_latest_properties: autoProperties,
    sources: selectedSources,
    language,
    section_order: sectionOrder,
    show_all_properties_cta: showAllPropertiesCta,
    lead_magnet_id: leadMagnetId || null,
  });

  const generatePreview = async () => {
    setPreviewLoading(true);
    setFlash(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletters/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(buildPayload()),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Preview mislukt');
      setPreviewHtml(d.html);
    } catch (e) {
      setFlash({ type: 'error', text: e.message });
    } finally { setPreviewLoading(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim() || !subject.trim()) {
      setFlash({ type: 'error', text: 'Vul een onderwerp en test-adres in.' });
      return;
    }
    setTesting(true);
    setFlash(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletters/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ...buildPayload(), to_email: testEmail.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Verzenden mislukt');
      setFlash({ type: 'success', text: `Test verstuurd naar ${testEmail} (Resend id: ${d.message_id})` });
    } catch (e) {
      setFlash({ type: 'error', text: e.message });
    } finally { setTesting(false); }
  };

  const broadcast = async () => {
    setShowConfirm(false);
    setBroadcasting(true);
    setFlash(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletters/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(buildPayload()),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Verzenden mislukt');
      setFlash({ type: 'success', text: `Verzenden gestart naar ${d.recipients_count} leads. Status update volgt.` });
      // Reset composer so the admin doesn't accidentally re-broadcast.
      setSubject(''); setPreheader(''); setBody('');
      setBlogSlugs([]); setPropertyIds([]);
      setAutoBlogs(2); setAutoProperties(3);
      setSelectedSources([]);
      setSectionOrder(['properties', 'blogs', 'download']);
      setShowAllPropertiesCta(true);
      setLeadMagnetId('');
      setLanguage('nl');
      setPreviewHtml('');
      refreshHistory();
    } catch (e) {
      setFlash({ type: 'error', text: e.message });
    } finally { setBroadcasting(false); }
  };

  /**
   * Load a previously sent newsletter into the composer as a starting point.
   * We deliberately do NOT copy the auto-append counters — a "template" is
   * about reusing the copy and hand-picked highlights, not shipping stale
   * "latest" content the admin didn't verify.
   */
  /** Suggest a preheader from the intro markdown. Strips headings and
   *  markdown syntax and takes the first ~140 chars — never overwrites
   *  what the admin already typed. */
  const suggestPreheader = () => {
    const stripped = (body || '')
      .replace(/^#+\s+.*$/gm, '')          // drop heading lines
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // link → text
      .replace(/[*_`>#-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!stripped) {
      setFlash({ type: 'error', text: 'Nog geen tekst om een preheader uit te destilleren.' });
      return;
    }
    const suggestion = stripped.length > 140 ? `${stripped.slice(0, 137)}…` : stripped;
    setPreheader(suggestion);
  };

  const moveSection = (idx, delta) => {
    setSectionOrder((prev) => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const SECTION_LABELS = {
    properties: 'Woningen',
    blogs: 'Blogs',
    download: 'Gratis download',
  };

  const applyAsTemplate = (n) => {
    setSubject(n.subject || '');
    setPreheader(n.preheader || '');
    setBody(n.body_markdown || '');
    setBlogSlugs(Array.isArray(n.blog_slugs) ? n.blog_slugs : []);
    setPropertyIds(Array.isArray(n.property_ids) ? n.property_ids.map(String) : []);
    setSelectedSources(Array.isArray(n.sources) ? n.sources : []);
    setAutoBlogs(0);
    setAutoProperties(0);
    if (n.language) setLanguage(n.language);
    if (Array.isArray(n.section_order) && n.section_order.length) {
      setSectionOrder(n.section_order);
    }
    setShowAllPropertiesCta(Boolean(n.show_all_properties_cta));
    setLeadMagnetId(n.lead_magnet_id || '');
    setPreviewHtml('');
    setFlash({
      type: 'success',
      text: `Sjabloon geladen uit "${n.subject}" — pas onderwerp en tekst aan voordat je verstuurt.`,
    });
    // Bring the composer back into view — history sits below.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ------------------------------------------------------------------ render
  const canSend = subject.trim() && body.trim() && recipientsCount > 0;

  return (
    <div data-testid="newsletter-panel">
      {/* Top-level tabs: pick between the mass-broadcast composer and
          the welcome-mail template that ships to every new subscriber. */}
      <div style={styles.tabBar} data-testid="newsletter-tabbar">
        <button
          type="button"
          onClick={() => setTab('broadcast')}
          style={{ ...styles.tabBtn, ...(tab === 'broadcast' ? styles.tabBtnActive : {}) }}
          data-testid="newsletter-tab-broadcast"
        >
          <Megaphone size={14} /> Broadcast
        </button>
        <button
          type="button"
          onClick={() => setTab('welcome')}
          style={{ ...styles.tabBtn, ...(tab === 'welcome' ? styles.tabBtnActive : {}) }}
          data-testid="newsletter-tab-welcome"
        >
          <Sparkles size={14} /> Welkomstmail
        </button>
      </div>

      {tab === 'welcome' && <WelcomeMailEditor />}
      {tab === 'broadcast' && (
      <>
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.h2}>Nieuwsbrief</h2>
          <p style={styles.sub}>
            Stel een mail samen en verstuur direct naar alle leads met GDPR-consent.
            Gebruikt Resend + auto unsubscribe.
          </p>
        </div>
        <div style={styles.recipientsBox} data-testid="newsletter-recipient-count">
          <Users size={16} color="#C5A059" />
          <div>
            <div style={styles.recipientsNum}>{recipientsCount ?? '—'}</div>
            <div style={styles.recipientsLabel}>ontvangers klaar</div>
          </div>
        </div>
      </div>

      {flash && (
        <div
          style={{ ...styles.flash, ...(flash.type === 'error' ? styles.flashError : styles.flashOk) }}
          data-testid={`newsletter-flash-${flash.type}`}
        >
          {flash.type === 'error' ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
          <span>{flash.text}</span>
        </div>
      )}

      <div style={styles.layout}>
        {/* ---------- COMPOSER ---------- */}
        <div style={styles.composer}>
          {/* Segmentation chips — pick which lead sources receive this
              broadcast. Empty selection = every consented lead. */}
          {sourceBreakdown.length > 0 && (
            <div style={styles.segmentWrap} data-testid="newsletter-segments">
              <div style={styles.segmentHeader}>
                <span style={styles.segmentLabel}>Segment</span>
                <span style={styles.segmentHelp}>
                  {selectedSources.length === 0
                    ? 'Iedereen met consent'
                    : `${selectedSources.length} bron${selectedSources.length === 1 ? '' : 'nen'} geselecteerd`}
                </span>
              </div>
              <div style={styles.segmentChips}>
                <button
                  type="button"
                  style={{
                    ...styles.segmentChip,
                    ...(selectedSources.length === 0 ? styles.segmentChipActive : {}),
                  }}
                  onClick={() => setSelectedSources([])}
                  data-testid="segment-chip-all"
                >
                  Alle bronnen · {sourceBreakdown.reduce((s, x) => s + x.count, 0)}
                </button>
                {sourceBreakdown.map((s) => {
                  const active = selectedSources.includes(s.source);
                  return (
                    <button
                      key={s.source}
                      type="button"
                      style={{
                        ...styles.segmentChip,
                        ...(active ? styles.segmentChipActive : {}),
                      }}
                      onClick={() => setSelectedSources((prev) => (
                        prev.includes(s.source)
                          ? prev.filter((x) => x !== s.source)
                          : [...prev, s.source]
                      ))}
                      data-testid={`segment-chip-${s.source}`}
                    >
                      {SOURCE_LABELS[s.source] || s.source} · {s.count}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Language selector — controls section labels & CTA copy. */}
          <div style={styles.langRow}>
            <span style={styles.autoLabel}>Taal van deze nieuwsbrief</span>
            <div style={styles.langChips}>
              {[{ v: 'nl', l: 'Nederlands' }, { v: 'en', l: 'English' }].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  style={{
                    ...styles.segmentChip,
                    ...(language === o.v ? styles.segmentChipActive : {}),
                  }}
                  onClick={() => setLanguage(o.v)}
                  data-testid={`newsletter-lang-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <label style={styles.label}>Onderwerp</label>
          <input
            style={styles.input}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Nieuwe woningen in Torrevieja & 3 tips voor je hypotheek"
            data-testid="newsletter-subject"
          />

          <label style={styles.label}>Preheader</label>
          <div style={styles.preheaderRow}>
            <input
              style={{ ...styles.input, margin: 0, flex: 1 }}
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
              placeholder="Deze week: 3 verse listings + de kosten van kopen in Spanje"
              data-testid="newsletter-preheader"
            />
            <button
              type="button"
              style={styles.suggestBtn}
              onClick={suggestPreheader}
              data-testid="newsletter-preheader-suggest"
              title="Voorstel op basis van intro-tekst"
            >
              Voorstel doen
            </button>
          </div>
          <p style={styles.hint}>
            Korte preview-tekst die naast of onder de onderwerpregel in de inbox verschijnt.
            Blijft altijd handmatig aanpasbaar en wordt nooit automatisch uit het onderwerp overgenomen.
          </p>

          <label style={styles.label}>Bericht (Markdown)</label>
          <div style={styles.toolbar}>
            <button style={styles.tbBtn} onClick={() => wrapSelection('**')} title="Vet" data-testid="tb-bold"><Bold size={14} /></button>
            <button style={styles.tbBtn} onClick={() => wrapSelection('*')} title="Cursief" data-testid="tb-italic"><Italic size={14} /></button>
            <button style={styles.tbBtn} onClick={() => wrapSelection('## ', '')} title="Kop" data-testid="tb-h2"><Heading2 size={14} /></button>
            <button style={styles.tbBtn} onClick={() => wrapSelection('- ', '')} title="Lijst" data-testid="tb-list"><List size={14} /></button>
            <button style={styles.tbBtn} onClick={insertLink} title="Link" data-testid="tb-link"><LinkIcon size={14} /></button>
          </div>
          <textarea
            ref={textareaRef}
            style={styles.textarea}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`# Beste lezer,\n\nWe hebben deze week 3 nieuwe listings toegevoegd...\n\nVeel plezier met bladeren!\n\nPascal`}
            rows={14}
            data-testid="newsletter-body"
          />

          <div style={styles.autoRow}>
            <div style={styles.autoBlock}>
              <span style={styles.autoLabel}>
                <Newspaper size={14} /> Automatisch nieuwste blogs
              </span>
              <select
                style={styles.select}
                value={autoBlogs}
                onChange={(e) => setAutoBlogs(Number(e.target.value))}
                data-testid="newsletter-auto-blogs"
              >
                <option value={0}>Uit</option>
                <option value={1}>1 blog</option>
                <option value={2}>2 blogs</option>
                <option value={3}>3 blogs</option>
                <option value={5}>5 blogs</option>
              </select>
            </div>
            <div style={styles.autoBlock}>
              <span style={styles.autoLabel}>
                <Home size={14} /> Automatisch nieuwste woningen
              </span>
              <select
                style={styles.select}
                value={autoProperties}
                onChange={(e) => setAutoProperties(Number(e.target.value))}
                data-testid="newsletter-auto-properties"
              >
                <option value={0}>Uit</option>
                <option value={1}>1 woning</option>
                <option value={2}>2 woningen</option>
                <option value={3}>3 woningen</option>
                <option value={5}>5 woningen</option>
              </select>
            </div>
          </div>

          {(pickedBlogsResolved.length > 0 || pickedPropsResolved.length > 0) && (
            <div style={styles.picked}>
              <div style={styles.pickedLabel}>Handmatig toegevoegd:</div>
              <div style={styles.pickedList}>
                {pickedBlogsResolved.map((b) => (
                  <div key={`b-${b.slug}`} style={styles.chip} data-testid={`chip-blog-${b.slug}`}>
                    <Newspaper size={12} /> {b.title}
                    <button style={styles.chipX} onClick={() => togglePickBlog(b.slug)}><X size={12} /></button>
                  </div>
                ))}
                {pickedPropsResolved.map((p) => (
                  <div key={`p-${p.id}`} style={styles.chip} data-testid={`chip-property-${p.id}`}>
                    <Home size={12} /> {p.title}
                    <button style={styles.chipX} onClick={() => togglePickProp(p.id)}><X size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* View-all-properties CTA toggle */}
          <label style={styles.optionRow} data-testid="cta-all-properties-wrap">
            <input
              type="checkbox"
              checked={showAllPropertiesCta}
              onChange={(e) => setShowAllPropertiesCta(e.target.checked)}
              style={styles.checkbox}
              data-testid="cta-all-properties-checkbox"
            />
            <span>
              <strong>&quot;{language === 'en' ? 'View all properties' : 'Bekijk alle woningen'}&quot;-knop tonen</strong>
              <span style={styles.hintInline}>
                {' '}— verschijnt direct onder de woning-kaarten, linkt naar /woningen
              </span>
            </span>
          </label>

          {/* Free-download lead-magnet block */}
          <div style={styles.magnetBox} data-testid="magnet-block">
            <label style={styles.optionRow}>
              <input
                type="checkbox"
                checked={Boolean(leadMagnetId)}
                onChange={(e) => setLeadMagnetId(
                  e.target.checked ? (leadMagnets[0]?.id || '') : ''
                )}
                style={styles.checkbox}
                data-testid="magnet-toggle"
              />
              <span><strong>Gratis download toevoegen</strong>
                <span style={styles.hintInline}> — promo-blok voor een CasaOthers lead magnet</span>
              </span>
            </label>
            {leadMagnetId && (
              <select
                style={{ ...styles.select, marginTop: '0.5rem' }}
                value={leadMagnetId}
                onChange={(e) => setLeadMagnetId(e.target.value)}
                data-testid="magnet-select"
              >
                {leadMagnets.map((m) => (
                  <option key={m.id} value={m.id}>
                    {language === 'en' && m.title_en ? m.title_en : m.title}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Section ordering */}
          <div style={styles.orderBox} data-testid="section-order">
            <div style={styles.orderHeader}>
              <span style={styles.autoLabel}>Volgorde van blokken</span>
              <span style={styles.hintInline}>Intro staat altijd bovenaan, footer altijd onderaan.</span>
            </div>
            <div style={styles.orderList}>
              <div style={{ ...styles.orderItem, ...styles.orderFixed }}>
                <span>1. Intro (jouw tekst)</span>
              </div>
              {sectionOrder.map((key, idx) => {
                const label = SECTION_LABELS[key] || key;
                return (
                  <div key={key} style={styles.orderItem} data-testid={`order-${key}`}>
                    <span>{idx + 2}. {label}</span>
                    <div style={styles.orderControls}>
                      <button
                        type="button"
                        style={styles.orderBtn}
                        onClick={() => moveSection(idx, -1)}
                        disabled={idx === 0}
                        data-testid={`order-up-${key}`}
                        title="Naar boven"
                      >↑</button>
                      <button
                        type="button"
                        style={styles.orderBtn}
                        onClick={() => moveSection(idx, 1)}
                        disabled={idx === sectionOrder.length - 1}
                        data-testid={`order-down-${key}`}
                        title="Naar beneden"
                      >↓</button>
                    </div>
                  </div>
                );
              })}
              <div style={{ ...styles.orderItem, ...styles.orderFixed }}>
                <span>{sectionOrder.length + 2}. Footer + uitschrijf-link</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={styles.actions}>
            <button style={styles.btnSecondary} onClick={generatePreview} disabled={isPreviewLoading} data-testid="newsletter-preview-btn">
              {isPreviewLoading ? <Loader2 size={16} className="spin" /> : <Eye size={16} />}
              Preview
            </button>
            <div style={styles.testGroup}>
              <input
                style={{ ...styles.input, margin: 0, flex: 1 }}
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Test-mail naar…"
                type="email"
                data-testid="newsletter-test-email"
              />
              <button style={styles.btnSecondary} onClick={sendTest} disabled={isTesting} data-testid="newsletter-test-btn">
                {isTesting ? <Loader2 size={16} className="spin" /> : <TestTube size={16} />}
                Test
              </button>
            </div>
            <button
              style={{ ...styles.btnPrimary, opacity: canSend && !isBroadcasting ? 1 : 0.5 }}
              disabled={!canSend || isBroadcasting}
              onClick={() => setShowConfirm(true)}
              data-testid="newsletter-send-btn"
            >
              {isBroadcasting ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
              Verzend naar {recipientsCount ?? 0} leads
            </button>
          </div>
        </div>

        {/* ---------- INSERT PANEL + PREVIEW ---------- */}
        <div style={styles.side}>
          <div style={styles.sideCard}>
            <div style={styles.sideHeader}>
              <Newspaper size={14} color="#C5A059" /> Nieuwste blogs
            </div>
            <div style={styles.sideList}>
              {recentBlogs.length === 0 && <div style={styles.empty}>Geen blogs gevonden.</div>}
              {recentBlogs.map((b) => {
                const active = blogSlugs.includes(b.slug);
                return (
                  <button
                    key={b.slug}
                    style={{ ...styles.sideItem, ...(active ? styles.sideItemActive : {}) }}
                    onClick={() => togglePickBlog(b.slug)}
                    data-testid={`pick-blog-${b.slug}`}
                  >
                    <div style={styles.sideItemTitle}>{b.title}</div>
                    <div style={styles.sideItemBody}>{b.excerpt}</div>
                    <div style={styles.sideItemAction}>
                      {active ? 'Toegevoegd ✓' : <><Plus size={12} /> Voeg toe</>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={styles.sideCard}>
            <div style={styles.sideHeader}>
              <Home size={14} color="#C5A059" /> Nieuwste woningen
            </div>
            <div style={styles.sideList}>
              {recentProps.length === 0 && <div style={styles.empty}>Geen woningen gevonden.</div>}
              {recentProps.map((p) => {
                const active = propertyIds.includes(String(p.id));
                return (
                  <button
                    key={p.id}
                    style={{ ...styles.sideItem, ...(active ? styles.sideItemActive : {}) }}
                    onClick={() => togglePickProp(p.id)}
                    data-testid={`pick-property-${p.id}`}
                  >
                    <div style={styles.sideItemTitle}>{p.title}</div>
                    <div style={styles.sideItemBody}>
                      {p.location} · € {p.price ? Number(p.price).toLocaleString('nl-NL') : '—'}
                    </div>
                    <div style={styles.sideItemAction}>
                      {active ? 'Toegevoegd ✓' : <><Plus size={12} /> Voeg toe</>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={styles.sideCard}>
            <div style={styles.sideHeader}>
              <Eye size={14} color="#C5A059" /> Live preview
            </div>
            {previewHtml ? (
              <iframe
                title="Newsletter preview"
                srcDoc={previewHtml}
                style={styles.previewFrame}
                data-testid="newsletter-preview-frame"
              />
            ) : (
              <div style={styles.previewEmpty}>Klik &quot;Preview&quot; om te zien hoe de mail eruit gaat zien.</div>
            )}
          </div>
        </div>
      </div>

      {/* ---------- HISTORY ---------- */}
      <div style={styles.historyCard}>
        <div style={styles.historyHeader}>
          <div style={styles.sideHeader}>Verzendgeschiedenis &amp; sjablonen</div>
          <span style={styles.historyHelp}>Klik &quot;Herbruiken&quot; om een verzonden mail als sjabloon te laden.</span>
        </div>
        {history.length === 0 && <div style={styles.empty}>Nog geen nieuwsbrieven verzonden.</div>}
        {history.map((n) => {
          const stats = n.stats || {};
          const opens = stats.opened || 0;
          const uOpens = stats.unique_opens || 0;
          const clicks = stats.clicked || 0;
          const uClicks = stats.unique_clicks || 0;
          const bounces = stats.bounced || 0;
          const delivered = stats.delivered || 0;
          const successCount = n.success_count || 0;
          // "Open rate" against delivered when we have webhook data,
          // else fall back to `success_count` (mail was queued at Resend).
          const denom = delivered || successCount || 0;
          const openRate = denom ? Math.round((uOpens / denom) * 100) : null;
          const clickRate = denom ? Math.round((uClicks / denom) * 100) : null;
          return (
            <div key={n.id} style={styles.historyRow} data-testid={`history-${n.id}`}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.historyTitle}>{n.subject}</div>
                <div style={styles.historyMeta}>
                  {new Date(n.created_at).toLocaleString('nl-NL')} · {n.recipients_count} ontvangers
                  {n.blog_count > 0 && ` · ${n.blog_count} blogs`}
                  {n.property_count > 0 && ` · ${n.property_count} woningen`}
                  {Array.isArray(n.sources) && n.sources.length > 0 && (
                    <> · <em>segment: {n.sources.map((s) => SOURCE_LABELS[s] || s).join(', ')}</em></>
                  )}
                </div>
                <div style={styles.statsRow} data-testid={`stats-${n.id}`}>
                  <span style={styles.statCell} title="Bezorgd">
                    <strong>{delivered || successCount}</strong> bezorgd
                  </span>
                  <span style={styles.statCell} title="Unieke opens / totaal opens">
                    <strong>{uOpens}</strong>/{opens} opens
                    {openRate !== null && <span style={styles.rate}> ({openRate}%)</span>}
                  </span>
                  <span style={styles.statCell} title="Unieke clicks / totaal clicks">
                    <strong>{uClicks}</strong>/{clicks} clicks
                    {clickRate !== null && <span style={styles.rate}> ({clickRate}%)</span>}
                  </span>
                  {bounces > 0 && (
                    <span style={{ ...styles.statCell, color: '#991B1B' }} title="Bounces">
                      <strong>{bounces}</strong> bounces
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', alignItems: 'flex-end' }}>
                <div style={{ ...styles.statusPill, ...statusStyle(n.status) }}>
                  {n.status === 'sending' && <Loader2 size={12} className="spin" />}
                  {n.status === 'sent' && `${n.success_count} verzonden`}
                  {n.status === 'partial' && `${n.success_count} OK · ${n.failure_count} fout`}
                  {n.status === 'failed' && 'Mislukt'}
                  {n.status === 'sending' && 'Bezig…'}
                </div>
                <button
                  style={styles.reuseBtn}
                  onClick={() => applyAsTemplate(n)}
                  data-testid={`reuse-${n.id}`}
                  title="Herbruik als sjabloon"
                >
                  <Copy size={12} /> Herbruiken
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ---------- CONFIRM MODAL ---------- */}
      {showConfirm && (
        <div style={styles.modalWrap} onClick={() => setShowConfirm(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()} data-testid="newsletter-confirm-modal">
            <h3 style={styles.modalTitle}>Verzenden bevestigen</h3>
            <p style={styles.modalBody}>
              Je staat op het punt om <strong>{recipientsCount}</strong> ontvangers te mailen met onderwerp:
              <br /><em>&quot;{subject}&quot;</em>
              <br /><br />
              Weet je zeker dat alles klopt? Deze actie kan niet ongedaan gemaakt worden.
            </p>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowConfirm(false)} data-testid="confirm-cancel">
                Annuleren
              </button>
              <button style={styles.btnPrimary} onClick={broadcast} data-testid="confirm-send">
                <Send size={14} /> Ja, verstuur
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
      </>
      )}
    </div>
  );
};

const statusStyle = (status) => {
  switch (status) {
    case 'sent': return { background: '#DCFCE7', color: '#166534' };
    case 'partial': return { background: '#FEF3C7', color: '#78350F' };
    case 'failed': return { background: '#FEE2E2', color: '#991B1B' };
    default: return { background: '#F1F5F9', color: '#334155' };
  }
};

const styles = {
  tabBar: {
    display: 'flex', gap: '0.35rem', marginBottom: '1.5rem',
    borderBottom: '1px solid #EAE5DD', paddingBottom: 0,
  },
  tabBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.65rem 1rem', background: 'transparent', color: '#585755',
    border: 'none', borderBottom: '2px solid transparent',
    fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer',
    marginBottom: '-1px', letterSpacing: '0.01em',
  },
  tabBtnActive: {
    color: '#1A1918', borderBottomColor: '#C5A059', fontWeight: 700,
  },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', margin: 0, color: '#1A1918' },
  sub: { color: '#585755', fontSize: '0.9rem', margin: '0.25rem 0 0' },
  recipientsBox: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    padding: '0.75rem 1rem', background: 'white', border: '1px solid #EAE5DD',
  },
  recipientsNum: { fontSize: '1.5rem', fontFamily: "'Playfair Display', serif", color: '#1A1918', lineHeight: 1 },
  recipientsLabel: { color: '#8C8B88', fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase' },

  flash: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem' },
  flashOk: { background: '#DCFCE7', color: '#166534', border: '1px solid #86EFAC' },
  flashError: { background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' },

  layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '1.5rem', alignItems: 'start' },

  composer: { background: 'white', border: '1px solid #EAE5DD', padding: '1.5rem' },
  segmentWrap: {
    marginBottom: '0.75rem', padding: '0.85rem 1rem',
    background: '#FAF6EE', border: '1px dashed #E4D9C1',
  },
  segmentHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: '0.75rem', marginBottom: '0.6rem', flexWrap: 'wrap',
  },
  segmentLabel: {
    fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase',
    color: '#8C6E28', fontWeight: 700,
  },
  segmentHelp: { fontSize: '0.75rem', color: '#8C8B88' },
  segmentChips: { display: 'flex', flexWrap: 'wrap', gap: '0.35rem' },
  segmentChip: {
    padding: '0.35rem 0.7rem', fontSize: '0.78rem',
    background: 'white', color: '#585755', border: '1px solid #E4D9C1',
    cursor: 'pointer', letterSpacing: '0.01em',
  },
  segmentChipActive: {
    background: '#1A1918', color: '#C5A059', borderColor: '#1A1918',
    fontWeight: 600,
  },

  langRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '1rem', marginBottom: '0.25rem', flexWrap: 'wrap',
  },
  langChips: { display: 'flex', gap: '0.35rem' },

  preheaderRow: { display: 'flex', gap: '0.5rem', alignItems: 'stretch' },
  suggestBtn: {
    padding: '0.55rem 0.85rem', background: '#FAF6EE', color: '#8C6E28',
    border: '1px solid #E4D9C1', fontSize: '0.78rem', fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  hint: {
    color: '#8C8B88', fontSize: '0.75rem', lineHeight: 1.55,
    margin: '0.35rem 0 0',
  },
  hintInline: { color: '#8C8B88', fontSize: '0.8rem', fontWeight: 400 },

  optionRow: {
    display: 'flex', gap: '0.55rem', alignItems: 'flex-start',
    marginTop: '1rem', cursor: 'pointer', color: '#1A1918',
    fontSize: '0.88rem', lineHeight: 1.55,
  },
  checkbox: {
    marginTop: '0.2rem', width: '16px', height: '16px', accentColor: '#C5A059',
    flexShrink: 0, cursor: 'pointer',
  },

  magnetBox: {
    marginTop: '1rem', padding: '0.85rem 1rem',
    background: '#FAF9F6', border: '1px solid #EAE5DD',
  },

  orderBox: {
    marginTop: '1.25rem', padding: '0.85rem 1rem',
    background: 'white', border: '1px solid #EAE5DD',
  },
  orderHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap',
  },
  orderList: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  orderItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.5rem 0.75rem', background: '#FAF9F6',
    border: '1px solid #EAE5DD', fontSize: '0.85rem', color: '#1A1918',
  },
  orderFixed: { background: '#F1EBE0', color: '#8C8B88', fontStyle: 'italic' },
  orderControls: { display: 'flex', gap: '0.2rem' },
  orderBtn: {
    width: '28px', height: '28px', border: '1px solid #D8D3CA',
    background: 'white', cursor: 'pointer', fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    color: '#1A1918',
  },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#1A1918', letterSpacing: '0.03em', marginTop: '1rem', marginBottom: '0.35rem' },
  input: { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #D8D3CA', fontSize: '0.95rem', background: 'white', boxSizing: 'border-box' },
  toolbar: { display: 'flex', gap: '0.25rem', marginBottom: '0.35rem' },
  tbBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.35rem 0.6rem', background: '#FAF9F6', border: '1px solid #EAE5DD', cursor: 'pointer', color: '#1A1918' },
  textarea: { width: '100%', padding: '0.75rem', border: '1px solid #D8D3CA', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.9rem', lineHeight: 1.55, boxSizing: 'border-box', resize: 'vertical' },

  autoRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' },
  autoBlock: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  autoLabel: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#585755' },
  select: { padding: '0.55rem 0.75rem', border: '1px solid #D8D3CA', background: 'white', fontSize: '0.9rem' },

  picked: { marginTop: '1rem', padding: '0.75rem', background: '#FAF6EE', border: '1px dashed #E4D9C1' },
  pickedLabel: { fontSize: '0.75rem', color: '#8C6E28', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.5rem' },
  pickedList: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.55rem', background: 'white', border: '1px solid #E4D9C1', fontSize: '0.8rem', color: '#1A1918' },
  chipX: { display: 'inline-flex', alignItems: 'center', border: 'none', background: 'transparent', cursor: 'pointer', color: '#8C6E28', padding: 0 },

  actions: { marginTop: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' },
  testGroup: { display: 'flex', gap: '0.5rem', flex: '1 1 300px' },
  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1.15rem', background: '#1A1918', color: '#C5A059', border: 'none', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' },
  btnSecondary: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', background: 'white', color: '#1A1918', border: '1px solid #D8D3CA', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },

  side: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  sideCard: { background: 'white', border: '1px solid #EAE5DD', padding: '1rem' },
  sideHeader: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#1A1918', fontWeight: 700, marginBottom: '0.75rem' },
  sideList: { display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '260px', overflowY: 'auto' },
  sideItem: { textAlign: 'left', width: '100%', border: '1px solid #EAE5DD', background: '#FAF9F6', padding: '0.6rem 0.75rem', cursor: 'pointer', color: '#1A1918' },
  sideItemActive: { background: '#FEF3C7', borderColor: '#FCD34D' },
  sideItemTitle: { fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.3, marginBottom: '0.2rem' },
  sideItemBody: { fontSize: '0.75rem', color: '#8C8B88', lineHeight: 1.4 },
  sideItemAction: { marginTop: '0.4rem', fontSize: '0.7rem', color: '#C5A059', letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' },

  previewFrame: { width: '100%', height: '480px', border: '1px solid #EAE5DD', background: 'white' },
  previewEmpty: { color: '#8C8B88', fontSize: '0.85rem', padding: '1rem 0' },
  empty: { color: '#8C8B88', fontSize: '0.85rem' },

  historyCard: { marginTop: '2rem', background: 'white', border: '1px solid #EAE5DD', padding: '1.25rem' },
  historyHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' },
  historyHelp: { color: '#8C8B88', fontSize: '0.75rem' },
  historyRow: { display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.65rem 0', borderTop: '1px solid #F1EBE0', flexWrap: 'wrap' },
  historyTitle: { fontSize: '0.95rem', color: '#1A1918', fontWeight: 600 },
  historyMeta: { fontSize: '0.75rem', color: '#8C8B88', marginTop: '0.15rem' },
  statsRow: {
    display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1.25rem',
    marginTop: '0.55rem', fontSize: '0.75rem', color: '#585755',
  },
  statCell: { display: 'inline-flex', alignItems: 'baseline', gap: '0.25rem' },
  rate: { color: '#8C8B88', marginLeft: '0.15rem' },
  statusPill: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.25rem 0.7rem', fontSize: '0.75rem', fontWeight: 700, borderRadius: '999px', letterSpacing: '0.02em' },
  reuseBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
    padding: '0.35rem 0.7rem', background: 'white', color: '#1A1918',
    border: '1px solid #D8D3CA', fontSize: '0.75rem', fontWeight: 600,
    cursor: 'pointer', letterSpacing: '0.02em',
  },

  modalWrap: { position: 'fixed', inset: 0, background: 'rgba(26,25,24,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' },
  modal: { background: 'white', maxWidth: '480px', width: '100%', padding: '1.5rem' },
  modalTitle: { fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', margin: '0 0 0.75rem', color: '#1A1918' },
  modalBody: { color: '#585755', fontSize: '0.95rem', lineHeight: 1.55, margin: '0 0 1.25rem' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' },
};

export default NewsletterPanel;
